import {
  EmailSchema,
  PagingSchema,
  PushSchema,
  SmsSchema,
  createDetailResponseSchema,
  createListResponseSchema,
  createListResponseSchemaDefault,
} from 'src/feature/bite/domain'
import z from 'zod'

export const SegmentSchema = z.object({
  macroSegment: z.string(),
  microSegment: z.string(),
  demographicInfos: z.any(),
  behavioralSegments: z.any(),
})

export const AddressSchema = z.object({
  buildingNo: z.string(),
  zipCode: z.string(),
  region: z.string(),
  city: z.string(),
  country: z.string(),
  addressLine: z.string(),
})

export const ConsentSchema = z.object({
  termsAndConditions: z.boolean().optional(),
  email: z.boolean(),
  sms: z.boolean(),
  push: z.boolean(),
})

export const TransactionSchema = z.object({
  orderId: z.string().nullable(),
  transactionId: z.string().nullable(),
  date: z.string(),
  type: z.any(),
  paidAmount: z.any(),
  totalAmount: z.any(),
  discountAmount: z.any(),
  channel: z.any(),
  posNo: z.string().nullable(),
  storeNo: z.string().nullable(),
})

export const ActivitySchema = z.object({
  type: z.any(),
  id: z.string().nullable(),
  packageName: z.string().nullable(),
  packageItem: z.string().nullable(),
  storeName: z.string().nullable(),
  credit: z.number().nullable(),
  createdOn: z.string().nullable(),
})

export const PackageSchema = z.object({
  id: z.string(),
  name: z.string(),
  amount: z.number(),
  status: z.number(),
  subscriptionOption: z.number(),
})

export const MemberSchema = z.object({
  middleName: z.string().nullable(),
  surname: z.string(),
  totalEarnings: z.number(),
  gender: z.number(),
  birthDate: z.string().nullable(),
  customerToken: z.string(),
  externalMemberId: z.string(),
  preferredLanguage: z.string(),
  secondLanguage: z.string(),
  memberId: z.string(),
  name: z.string(),
  netPoints: z.number(),
  worth: z.number(),
  savings: z.number(),
  mobile: z.string().nullable(),
  email: z.string().nullable(),
  enrollmentDate: z.string(),
  status: z.number(),
  storeName: z.string(),
  storeId: z.string(),
  customerGroup: z.string(),
  customerID: z.string(),
})

export const MemberDetailSchema = z.object({
  birthDate: z.string().nullable(),
  customerGroup: z.string(),
  customerToken: z.string(),
  email: z.string(),
  enrollmentDate: z.string(),
  externalMemberId: z.string(),
  id: z.string(),
  memberId: z.string(),
  mobile: z.string(),
  name: z.string().nullable(),
  netPoints: z.number(),
  referedCustomerCode: z.string().nullable(),
  referedCustomerID: z.string().nullable(),
  referralCode: z.string().nullable(),
  savings: z.number(),
  status: z.number(),
  surname: z.string().nullable(),
  totalEarnings: z.number(),
  vipTier: z.string().nullable(),
  worth: z.number(),
  customerID: z.string(),
  countryCode: z.string().nullable().optional(),
})

export const PackageDetailSchema = z.object({
  packageItemList: z.array(
    z.object({
      id: z.string(),
      count: z.number(),
      usedCount: z.number(),
      remainingCount: z.number(),
      order: z.number(),
      name: z.string(),
      status: z.number(),
      code: z.string(),
      createdOn: z.string(),
      credit: z.number(),
      memberSubscriptionPackageID: z.string(),
      remainingCredit: z.number(),
      usedCredit: z.number(),
    })
  ),
  pricingDetail: z
    .object({
      price: z.number(),
      subscriptionOptions: z.number(),
      count: z.number(),
      recurring: z.boolean(),
      taxRate: z.number(),
      purchaseType: z.number(),
      cancellationFee: z.number(),
    })
    .optional()
    .nullable(),
  packageDetail: z
    .object({
      id: z.string().nullable(),
      name: z.string().nullable(),
      startDate: z.string().nullable(),
      endDate: z.string().nullable(),
      status: z.number().nullable(),
      createdOn: z.string().nullable(),
      modifiedOn: z.string().nullable(),
      memberID: z.string().nullable(),
      merchantID: z.string().nullable(),
      price: z.number().nullable(),
      priceBase: z.number().nullable(),
      subscriptionPackageID: z.string().nullable(),
    })
    .optional()
    .nullable(),
})

export const PackagePaymentListSchema = z.array(
  z.object({
    paymentDate: z.string().nullable(),
    amount: z.number(),
    channel: z.number(),
    isPaid: z.boolean(),
    status: z.number().nullable().optional(),
  })
)

export const PackagePaymentListResponseSchema = createListResponseSchema(PackagePaymentListSchema)

export const MemberTabSchema = z.object({
  Consent: ConsentSchema.optional(),
  Segment: SegmentSchema.optional(),
  Activities: z.object({dataList: ActivitySchema.array(), paging: PagingSchema}).optional(),
  Address: z.object({dataList: AddressSchema.array(), paging: PagingSchema}).optional(),
  Transaction: z.object({dataList: TransactionSchema.array(), paging: PagingSchema}).optional(),
  Packages: z.object({dataList: PackageSchema.array(), paging: PagingSchema}).optional().nullable(),
  // Engagement
  SmsEngagement: z.object({dataList: SmsSchema.array(), paging: PagingSchema}).optional(),
  PushEngagement: z.object({dataList: PushSchema.array(), paging: PagingSchema}).optional(),
  EmailEngagement: z.object({dataList: EmailSchema.array(), paging: PagingSchema}).optional(),
})

export const Responses = {
  Segment: createDetailResponseSchema(SegmentSchema),
  // Address: createDetailResponseSchema(z.object({ addresses: AddressSchema.array() })),
  Address: createListResponseSchema(AddressSchema.array()),
  Consent: createDetailResponseSchema(ConsentSchema),
  Transaction: createListResponseSchema(TransactionSchema.array()),
  Activities: createListResponseSchema(ActivitySchema.array()),
  Packages: createListResponseSchema(PackageSchema.array()),
  SmsEngagement: createListResponseSchemaDefault(
    z.object({
      emails: EmailSchema.array(),
      pushes: PushSchema.array(),
      textMessages: SmsSchema.array(),
      listPaging: PagingSchema,
    })
  ),
  PushEngagement: createListResponseSchemaDefault(
    z.object({
      emails: EmailSchema.array(),
      pushes: PushSchema.array(),
      textMessages: SmsSchema.array(),
      listPaging: PagingSchema,
    })
  ),
  EmailEngagement: createListResponseSchemaDefault(
    z.object({
      emails: EmailSchema.array(),
      pushes: PushSchema.array(),
      textMessages: SmsSchema.array(),
      listPaging: PagingSchema,
    })
  ),
}

export const MemberSchemaResponse = createDetailResponseSchema(
  z
    .object({
      member: MemberDetailSchema,
    })
    .nullable()
)

export const PackageDetailSchemaResponse = createDetailResponseSchema(PackageDetailSchema)

export type MemberTab = z.infer<typeof MemberTabSchema>

export type Member = z.infer<typeof MemberSchema>
export type MemberDetail = z.infer<typeof MemberDetailSchema>
export type PackageDetail = z.infer<typeof PackageDetailSchema>
export type PackagePaymentList = z.infer<typeof PackagePaymentListResponseSchema>
export type PackagePaymentListPaging = z.infer<typeof PagingSchema>

export type MemberResponse = z.infer<typeof MemberSchemaResponse>
export type PackagePaymentListResponse = z.infer<typeof PackagePaymentListResponseSchema>
export type PackageDetailResponse = z.infer<typeof PackageDetailSchemaResponse>
